/**
 * Planning priorities — entity-configured list (§1E / §4.7).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  GitBranch,
  List,
  Loader2,
  ExternalLink,
  Pencil,
  Trash2,
  X,
  ListOrdered,
} from 'lucide-react';
import type { SortOption, TableColumnWithRender, FacetDefinition } from '../../../components/collection/types';
import { ItemImportExportModal } from '../../../components/prioritization/item-list/ItemImportExportModal';
import { PriorityRankReorderModal } from './PriorityRankReorderModal';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import {
  createPriority,
  deletePriority,
  engineInputKindLabel,
  applyPlanningOverride,
  acceptedRanksForPlanCycle,
  getPlanningOverrides,
  getPriorities,
  getPriorityDependencies,
  computeReorderedByRank,
  reorderPriorities,
  upsertPriorityOverride,
} from '../../../lib/planningEngineService';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { usePlanningEngine } from './PlanningEngineContext';
import { PlanningEntityListPage } from './PlanningEntityListPage';
import type { Priority, PriorityDependency, PrioritySummary } from '../../../lib/planningEngineModule.types';
import { WORK_DEFINITION_STATUS_LABELS } from '../../../lib/planningEngineModule.types';
import {
  PLANNING_SYNC_DEP_NOTE_PREFIX,
  WORK_SIZE_BAND_LABELS,
  itemToPriority,
  planEffortDivergence,
  resolveItemEffortCapacityUnits,
} from '../../../lib/itemToPlanningAdapter';
import type { Item } from '../../../types/prioritization/lifecycle';
import { exportCsv } from './planningEngineViewShared';
import { useResourceRosterEnabled } from '../../../hooks/useResourceRosterEnabled';
import RequirementsPanel from './RequirementsPanel';
import { useResourceTypesStore } from '../../../stores/prioritization/resourceTypesStore';
import {
  getItemEffortBandHours,
  usePlanningDefaultsStore,
} from '../../../stores/prioritization/planningDefaultsStore';
import { useItemHref } from '@/lib/itemLinkScope';

function feasibilityBadge(f: string | undefined) {
  if (!f) return null;
  const cls =
    f === 'Fully Achievable'
      ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]'
      : f === 'Partially Achievable'
        ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]'
        : f === 'Not Achievable'
          ? 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]'
          : 'bg-[var(--surface)] text-[var(--text-sec)] dark:bg-[var(--bg)]/20';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{f}</span>
  );
}

function PriorityDetailPanel({
  priority,
  projectId: contextProjectId,
  sourceItem,
  summary,
  inboundDeps,
  outboundDeps,
  priorityById,
  onUpdated,
  onDeleted,
}: {
  priority: Priority;
  projectId: string;
  sourceItem?: Item;
  summary?: PrioritySummary;
  inboundDeps: PriorityDependency[];
  outboundDeps: PriorityDependency[];
  priorityById: Map<string, Priority>;
  onUpdated: (saved: Priority, moveToRank?: number) => void;
  onDeleted: () => void;
}) {
  const itemHref = useItemHref();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(priority);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rosterEnabled = useResourceRosterEnabled();
  const resourceTypes = useMemo(() => useResourceTypesStore.getState().getEnabledTypes(), []);

  // Item Effort / altitude defaults → capacity units (CU)
  const bandHours = usePlanningDefaultsStore((s) => s.defaults.itemEffortBandHours);
  const resolvedEffort = sourceItem
    ? resolveItemEffortCapacityUnits(sourceItem, bandHours)
    : null;
  const workSizeLabel = resolvedEffort?.workSizeBand
    ? WORK_SIZE_BAND_LABELS[resolvedEffort.workSizeBand as keyof typeof WORK_SIZE_BAND_LABELS]
    : null;
  const estimatedHours = resolvedEffort?.hours ?? 0;
  const derivedCuEstimate = resolvedEffort?.capacityUnits ?? null;
  // The plan's own copy of the size vs what the item says today. This used to
  // be labelled "manually set", which asserted a human act nobody could verify:
  // the overlay row is written in bulk when the plan is built as well as when
  // somebody types a number, and the two are indistinguishable afterwards. The
  // true statement is that the numbers differ.
  const divergence = planEffortDivergence(priority.priorityLevelWuEstimate, sourceItem, bandHours);
  const draftDivergence = planEffortDivergence(draft.priorityLevelWuEstimate, sourceItem, bandHours);
  const defaultFromAltitude = resolvedEffort?.isDefaultFromAltitude ?? false;
  const nonIntentional = resolvedEffort?.isNonIntentional ?? false;

  useEffect(() => {
    setDraft(priority);
    setEditing(false);
  }, [priority]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Rank moves are handled as a renumber over the whole visible list, not
      // a plain single-row write here — writing this row's forceRank directly
      // is exactly how two priorities end up sharing the same rank. Save this
      // row's OTHER fields against its CURRENT rank, then hand the actual
      // move off to the parent (which has the full ordered list) via
      // onUpdated's moveToRank.
      const rankChanged = draft.forceRank !== priority.forceRank && draft.forceRank != null;
      // Always bind to the planning workspace id — live-item priorities may carry
      // an empty/stale projectId from item.workspace_id and then fail to reload.
      const toSave: Priority = {
        ...draft,
        projectId: contextProjectId,
        forceRank: rankChanged ? priority.forceRank : draft.forceRank,
      };
      await upsertPriorityOverride(toSave);

      // Live items own the title. planning_priorities.name is a mirror; without
      // this write, reload rebuilds from items and the title silently reverts.
      // Item Effort (CU) is written by upsertPriorityOverride — mirror into the store.
      if (sourceItem) {
        const title =
          draft.name.trim() && draft.name.trim() !== sourceItem.title
            ? draft.name.trim()
            : null;
        if (title) {
          const { error: titleError } = await getSupabaseClient()
            .from('items')
            .update({ title, updated_at: new Date().toISOString() })
            .eq('id', sourceItem.id);
          if (titleError) throw titleError;
        }
        const cu = toSave.priorityLevelWuEstimate;
        useItemsStore.setState((state) => ({
          items: state.items.map((i) =>
            i.id === sourceItem.id
              ? {
                  ...i,
                  ...(title ? { title, updatedAt: new Date() } : {}),
                  ...(cu != null && cu > 0
                    ? {
                        effortCapacityUnits: cu,
                        effortSource: 'planner_override' as const,
                        updatedAt: new Date(),
                      }
                    : {}),
                }
              : i,
          ),
        }));
        // A plan in force may have kept the item's effort as it was: show what the server holds.
        if (cu != null && cu > 0) void useItemsStore.getState().loadItemById(sourceItem.id);
      }

      setEditing(false);
      onUpdated(toSave, rankChanged ? (draft.forceRank ?? undefined) : undefined);
    } catch (err) {
      // Supabase PostgrestError is not an Error instance — extract .message.
      const msg =
        err instanceof Error
          ? err.message
          : (err as { message?: string })?.message ?? String(err);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Pull the item's current size into the plan — deliberately, one priority at
   * a time. Nothing refreshes on its own: a plan that re-costs itself while
   * nobody is looking is how the cut line moves without a decision.
   */
  const adoptItemSize = async () => {
    if (!divergence) return;
    setSaving(true);
    setError(null);
    try {
      const toSave: Priority = {
        ...priority,
        projectId: contextProjectId,
        priorityLevelWuEstimate: divergence.itemCapacityUnits,
      };
      const saved = await upsertPriorityOverride(toSave);
      setDraft(saved);
      onUpdated(saved);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : (err as { message?: string })?.message ?? String(err);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm('Delete this planning priority?')) return;
    setSaving(true);
    try {
      await deletePriority(priority.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-5 space-y-4 h-full overflow-auto">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {editing ? (
            <input
              className="w-full text-lg font-semibold border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          ) : (
            <h3 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
              {priority.name}
            </h3>
          )}
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1 font-mono truncate">
            {priority.id}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {editing ? (
            <>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft(priority);
                  setEditing(false);
                }}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="p-2 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
                title="Edit"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => void remove()}
                disabled={saving}
                className="p-2 text-[var(--text-ter)] hover:text-[var(--coral)]"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-[var(--coral)] dark:text-[var(--coral)]">{error}</p>}

      <div className="flex items-center gap-3 flex-wrap">
        <Link
          to={itemHref(priority.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open in Priorities
        </Link>
        <Link
          to={itemHref(priority.id, 'relationships')}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:text-primary-600 dark:hover:text-primary-400 hover:underline"
        >
          Relationships ↗
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Rank</p>
          {editing ? (
            <input
              type="number"
              className="w-full mt-0.5 border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1 text-sm"
              value={draft.forceRank ?? ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  forceRank: e.target.value ? Number(e.target.value) : null,
                })
              }
            />
          ) : (
            <p className="font-medium">{priority.forceRank ?? '—'}</p>
          )}
        </div>
        <div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Decomposition</p>
          <p className="font-medium">{WORK_DEFINITION_STATUS_LABELS[priority.workDefinitionStatus]}</p>
        </div>
        <div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Feasibility</p>
          {feasibilityBadge(summary?.feasibility) ?? <span>—</span>}
        </div>
        <div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Resource type</p>
          {editing ? (
            <select
              className="w-full mt-0.5 border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1 text-sm bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
              value={draft.priorityResourceType ?? 'engineering'}
              onChange={(e) => setDraft({ ...draft, priorityResourceType: e.target.value })}
            >
              {resourceTypes.map((rt) => (
                <option key={rt.key} value={rt.key}>{rt.label}</option>
              ))}
            </select>
          ) : (
            <p className="font-medium">
              {resourceTypes.find((rt) => rt.key === priority.priorityResourceType)?.label ?? priority.priorityResourceType ?? '—'}
            </p>
          )}
        </div>
      </div>

      {/* ── Effort section (capacity units) ─────────────────────────────── */}
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)] p-3 space-y-2">
        <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide">
          Item Effort (capacity units)
        </p>
        {workSizeLabel || derivedCuEstimate != null ? (
          <div className="flex items-center gap-2 flex-wrap">
            {workSizeLabel && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)]">
                {workSizeLabel}
                {nonIntentional ? '*' : ''}
              </span>
            )}
            <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              ≈ {estimatedHours} hrs · {derivedCuEstimate ?? '—'} CU
              {defaultFromAltitude ? ' · Default from item altitude' : ''}
              {nonIntentional ? ' *' : ''}
            </span>
          </div>
        ) : (
          <div className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] space-y-0.5">
            <p className="font-medium">No Item Effort or altitude size yet.</p>
            <p className="text-[var(--mustard)] dark:text-[var(--mustard)]">
              In Priorities: open the item → Classification → set altitude Work Size (or pick altitude 1–5). Or set capacity units below.
            </p>
          </div>
        )}
        {nonIntentional && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            * Size inferred from altitude level — Work Size was not intentionally answered.
          </p>
        )}

        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Priority-level capacity demand (CU)</p>
            {divergence && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 text-[var(--mustard)] dark:text-[var(--mustard)] font-medium">
                differs from the item
              </span>
            )}
          </div>
          <p className="text-xs text-[var(--text-ter)] mb-1">
            How many capacity units this priority needs in total — a rough estimate, not a work-package decomposition.
            {' '}Saving writes Item Effort on the item; it does not change altitude Work Size.
          </p>
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                step={1}
                className="w-24 border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1 text-base font-mono"
                value={draft.priorityLevelWuEstimate ?? ''}
                placeholder={derivedCuEstimate ? String(derivedCuEstimate) : '—'}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    priorityLevelWuEstimate: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
              <span className="text-xs text-[var(--text-ter)]">CU total</span>
              {draftDivergence && derivedCuEstimate !== null && (
                <button
                  type="button"
                  onClick={() => setDraft({ ...draft, priorityLevelWuEstimate: derivedCuEstimate })}
                  className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline"
                >
                  Use the item&rsquo;s size ({derivedCuEstimate} CU)
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="font-medium text-sm">
                {priority.priorityLevelWuEstimate != null
                  ? `${priority.priorityLevelWuEstimate} CU`
                  : <span className="text-[var(--text-ter)]">—</span>}
              </p>
              {divergence && (
                <div className="mt-1.5 space-y-1">
                  <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                    The item now says {divergence.itemCapacityUnits} CU
                    {workSizeLabel ? ` (${workSizeLabel})` : ''}. The plan charges{' '}
                    {divergence.planCapacityUnits} CU and will keep charging it until you say
                    otherwise.
                  </p>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void adoptItemSize()}
                    className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline disabled:opacity-50"
                  >
                    {saving
                      ? 'Applying…'
                      : `Use the item's size (${divergence.itemCapacityUnits} CU)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {!editing && derivedCuEstimate == null && !priority.priorityLevelWuEstimate && (
          <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)] font-medium">
            No demand estimate — engine will mark this Needs Sizing. Set CU above or set altitude Work Size on the item.
          </p>
        )}
      </div>

      {/* What this priority needs from whoever does it. Optional throughout —
          declaring nothing plans against the whole pool, exactly as before. */}
      {rosterEnabled && contextProjectId && (
        <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] pt-4">
          <RequirementsPanel workspaceId={contextProjectId} priorityId={priority.id} />
        </div>
      )}

      <div>
        <p className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-2">
          Dependencies (priority grain)
        </p>
        {outboundDeps.length === 0 && inboundDeps.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">No priority-level dependencies.</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {outboundDeps.map((d) => (
              <li key={d.id} className="flex items-start gap-2 text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                <GitBranch className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Blocked on{' '}
                  <span className="font-medium">
                    {priorityById.get(d.toPriorityId)?.name ?? d.toPriorityId}
                  </span>
                  {d.notes?.startsWith(PLANNING_SYNC_DEP_NOTE_PREFIX) && (
                    <span className="text-xs text-[var(--text-ter)] ml-1">(from item relationship)</span>
                  )}
                </span>
              </li>
            ))}
            {inboundDeps.map((d) => (
              <li key={d.id} className="flex items-start gap-2 text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                <GitBranch className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-[var(--text-ter)]" />
                <span>
                  <span className="font-medium">
                    {priorityById.get(d.fromPriorityId)?.name ?? d.fromPriorityId}
                  </span>{' '}
                  depends on this
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Create modal ────────────────────────────────────────────────────────────

function CreatePriorityModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { projectId, activeScenario } = usePlanningEngine();
  const [name, setName] = useState('');
  const [rank, setRank] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim() || !projectId) return;
    setSaving(true);
    setError(null);
    try {
      await createPriority(projectId, {
        name: name.trim(),
        forceRank: rank ? Number(rank) : null,
        scenarioId: activeScenario?.id ?? null,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2">
            <ListOrdered className="h-5 w-5 text-[var(--text-ter)]" />
            <h3 className="text-base font-semibold text-[var(--text-pri)]">New priority</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-[var(--text-ter)]">
            Creates an unbound priority — not linked to a Prioritization item. Best used for planning-only items or manual overrides.
          </p>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Name <span className="text-[var(--coral)]">*</span></label>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleSave(); }}
              placeholder="e.g. Payments foundation"
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Force rank <span className="text-[var(--text-ter)] font-normal">(optional)</span></label>
            <input
              type="number"
              min={1}
              value={rank}
              onChange={(e) => setRank(e.target.value)}
              placeholder="e.g. 1"
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-[var(--text-ter)] mt-1">Lower numbers = higher priority. Leave blank to append at end.</p>
          </div>
          {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--rule)]">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]">Cancel</button>
          <button type="button" onClick={() => void handleSave()} disabled={saving || !name.trim()} className="px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Create priority
          </button>
        </div>
      </div>
    </div>
  );
}

const PRIORITY_FACETS: FacetDefinition<string>[] = [
  {
    key: 'workDefinitionStatus',
    label: 'Decomposition',
    options: Object.entries(WORK_DEFINITION_STATUS_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  },
];

export default function PlanningPrioritiesView() {
  const itemHref = useItemHref();
  const { projectId, activeScenario, planOutput, engineInput, scopedItemIds } = usePlanningEngine();
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [priorityDeps, setPriorityDeps] = useState<PriorityDependency[]>([]);
  // Item ids whose rank was set deliberately in planning, as opposed to coming
  // from the ranking the organization accepted. Both are real ranks; only one
  // of them is somebody overruling the room, and the column should say which.
  const [overriddenRankIds, setOverriddenRankIds] = useState<ReadonlySet<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('priority') ?? null;
  const setSelectedId = useCallback(
    (id: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (id) {
          next.set('priority', id);
        } else {
          next.delete('priority');
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );
  const [importOpen, setImportOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const storeItems = useItemsStore((s) => s.items);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);

  const summaryByPriorityId = useMemo(
    () => new Map((planOutput?.prioritySummaries ?? []).map((s) => [s.priority.id, s])),
    [planOutput],
  );
  const priorityById = useMemo(() => new Map(priorities.map((p) => [p.id, p])), [priorities]);

  // Items ARE priorities — no sync step. Load items from the store and overlay
  // any engine-specific overrides (force rank, above/below line) from planning_priorities.
  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      // Always fetch items fresh — the store may hold items from a previous workspace
      // or a session where a different scope was active.
      const [overrides, deps, allDbPriorities, , acceptedRanks] = await Promise.all([
        getPlanningOverrides(projectId),
        getPriorityDependencies(projectId),
        getPriorities(projectId),
        fetchItems(),
        // The ranking the organization accepted — the same source the engine
        // orders the plan by (#785). Without it this grid showed Rank "—" on
        // every row while the plan underneath was ranked, so the column could
        // not show the order actually driving the plan.
        acceptedRanksForPlanCycle(activeScenario?.planCycleId),
      ]);
      const liveItems = useItemsStore.getState().items;

      // Build priority list from live items, overlaying any saved overrides.
      const liveItemIds = new Set(liveItems.map((i) => i.id));
      const bandHours = getItemEffortBandHours();
      const overridden = new Set<string>();
      const mapped: Priority[] = liveItems
        .filter((item) => item.exit_status !== 'archived')
        .map((item) => {
          const accepted = acceptedRanks.get(item.id) ?? null;
          const base = itemToPriority(item, activeScenario?.id ?? null, bandHours, accepted);
          // Ensure overlay lookup uses the same project_id we write on Save.
          const withProject = base.projectId ? base : { ...base, projectId: projectId };
          const override = overrides.get(item.id);
          if (override?.forceRank != null && override.forceRank !== accepted) {
            overridden.add(item.id);
          }
          return applyPlanningOverride(withProject, override);
        });
      setOverriddenRankIds(overridden);

      // Append unbound priorities — rows in planning_priorities with no matching live item.
      // These are created via the "New priority" button on this tab.
      const unboundPriorities = allDbPriorities.filter((p) => !liveItemIds.has(p.id));
      const combined = [...mapped, ...unboundPriorities].sort((a, b) => {
        if (a.forceRank != null && b.forceRank != null) return a.forceRank - b.forceRank;
        if (a.forceRank != null) return -1;
        if (b.forceRank != null) return 1;
        return 0;
      });

      setPriorities(combined);
      setPriorityDeps(deps);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, activeScenario?.id, activeScenario?.planCycleId, fetchItems]);

  useEffect(() => { void load(); }, [load]);

  // Re-load when the scope changes so that newly-scoped items are always reflected
  // even if the component was already mounted (e.g. user picked a pool on Overview
  // then switched to this tab without the component unmounting).
  const prevScopedItemIdsRef = useRef<Set<string> | null>(undefined as unknown as Set<string> | null);
  useEffect(() => {
    if (prevScopedItemIdsRef.current === undefined) {
      prevScopedItemIdsRef.current = scopedItemIds;
      return;
    }
    if (prevScopedItemIdsRef.current !== scopedItemIds) {
      prevScopedItemIdsRef.current = scopedItemIds;
      void load();
    }
  }, [scopedItemIds, load]);

  const handleImportClose = useCallback(async () => {
    setImportOpen(false);
    await Promise.all([fetchItems(), fetchRelationships()]);
    await load();
  }, [fetchItems, fetchRelationships, load]);

  /**
   * Move `moved` to rank `moveToRank` and renumber everyone else in the
   * currently-visible (scope-filtered) set to match — the reorder operates
   * on the exact set the rank numbers are displayed against on screen, not
   * the unfiltered project-wide list.
   */
  const handleReorderByRank = useCallback(
    async (moved: Priority, moveToRank: number) => {
      const visible = scopedItemIds ? priorities.filter((p) => scopedItemIds.has(p.id)) : priorities;
      const withMoved = visible.map((p) => (p.id === moved.id ? moved : p));
      const sorted = computeReorderedByRank(withMoved, moved.id, moveToRank);

      const updated = await reorderPriorities(sorted);
      const byId = new Map(updated.map((p) => [p.id, p]));
      setPriorities((prev) => prev.map((p) => byId.get(p.id) ?? p));
    },
    [priorities, scopedItemIds],
  );


  // Filter to the active input scope when one is selected.
  // priority.id === item.id (the adapter preserves item UUIDs as priority IDs).
  const visiblePriorities = useMemo(() => {
    if (!scopedItemIds) return priorities;
    console.debug('[PlanningPrioritiesView] scope filter', {
      scopedCount: scopedItemIds.size,
      priorityCount: priorities.length,
      sampleScopedIds: Array.from(scopedItemIds).slice(0, 3),
      samplePriorityIds: priorities.slice(0, 3).map((p) => p.id),
    });
    return priorities.filter((p) => scopedItemIds.has(p.id));
  }, [priorities, scopedItemIds]);

  const scopeBanner = scopedItemIds
    ? `Scoped to ${engineInputKindLabel(engineInput.type)} — showing ${visiblePriorities.length} of ${priorities.length} priorities`
    : null;

  const sortOptions: SortOption<'forceRank' | 'name'>[] = [
    { field: 'forceRank', label: 'Rank', defaultDirection: 'asc' },
    { field: 'name', label: 'Name', defaultDirection: 'asc' },
  ];

  const itemById = useMemo(
    () => new Map(storeItems.map((i) => [i.id, i])),
    [storeItems],
  );

  // Workspace band→hours overrides, so "what the item says today" is resolved
  // the same way here, in the detail panel and in the engine.
  const bandHours = usePlanningDefaultsStore((s) => s.defaults.itemEffortBandHours);

  // How many priorities charge a number their item no longer agrees with.
  // Nothing recomputes on its own; this only makes the gap countable.
  const divergentCount = useMemo(
    () =>
      priorities.filter(
        (p) => planEffortDivergence(p.priorityLevelWuEstimate, itemById.get(p.id), bandHours) != null,
      ).length,
    [priorities, itemById, bandHours],
  );

  const tableColumns: TableColumnWithRender<Priority>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        sortable: true,
        minWidth: 240,
        flex: true,
        render: (p) => (
          <Link
            to={itemHref(p.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:text-primary-600 dark:hover:text-primary-400 hover:underline block min-w-0 w-full"
            title={p.name}
          >
            {p.name}
          </Link>
        ),
      },
      {
        key: 'forceRank',
        label: 'Rank',
        sortable: true,
        align: 'right',
        minWidth: 64,
        render: (p) => (
          <span className="tabular-nums">
            {p.forceRank ?? '—'}
            {overriddenRankIds.has(p.id) && (
              <span
                className="ml-1 text-[var(--mustard)] dark:text-[var(--mustard)]"
                title="Set in planning, not the rank the organization accepted."
              >
                *
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'effort',
        label: 'Effort',
        // Wide enough for the band plus both numbers when the plan's copy and
        // the item disagree — at 100 the cell truncated to "3–8 wk 2…".
        minWidth: 190,
        render: (p) => {
          const item = itemById.get(p.id);
          const ws = item?.altitudeScopingInputs?.workSize;
          const band = ws ? WORK_SIZE_BAND_LABELS[ws as keyof typeof WORK_SIZE_BAND_LABELS] : null;
          const wu = p.priorityLevelWuEstimate;
          if (!band && !wu) return <span className="text-[var(--text-ter)] text-xs">Not set</span>;
          // The band describes the ITEM; the CU number is what the PLAN charges.
          // They were printed side by side as if they were one fact, so a stale
          // plan number sat next to a fresh band and the cell contradicted
          // itself. When they disagree, say whose number is whose.
          const divergence = planEffortDivergence(wu, item, bandHours);
          return (
            <span className="text-xs">
              {band && (
                <span className="inline-block px-1.5 py-0.5 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] font-medium mr-1.5">
                  {band}
                </span>
              )}
              {wu != null && (
                <span className="text-[var(--text-ter)] tabular-nums">
                  {divergence ? `plan ${wu} CU` : `${wu} CU`}
                </span>
              )}
              {divergence && (
                <span
                  className="ml-1.5 text-[var(--mustard)] dark:text-[var(--mustard)] tabular-nums"
                  title={`The plan charges ${divergence.planCapacityUnits} CU. The item now says ${divergence.itemCapacityUnits} CU. Open the priority to use the item's number.`}
                >
                  · item {divergence.itemCapacityUnits} CU
                </span>
              )}
            </span>
          );
        },
      },
      {
        key: 'feasibility',
        label: 'Feasibility',
        minWidth: 120,
        render: (p) => feasibilityBadge(summaryByPriorityId.get(p.id)?.feasibility),
      },
    ],
    [summaryByPriorityId, itemById, bandHours, overriddenRankIds, itemHref],
  );

  return (
    <>
      <PlanningEntityListPage
        items={visiblePriorities}
        loading={loading}
        getId={(p) => p.id}
        title="Priorities"
        subtitle={[
          scopeBanner ?? 'Your active Prioritization items — filtered by the selected input scope',
          divergentCount > 0
            ? `${divergentCount} of ${priorities.length} cost a different size than their item — open one to use the item's number.`
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        icon={<List className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBgClassName="bg-[var(--slate-dim)]"
        searchFields={['name', 'category']}
        sortOptions={sortOptions}
        defaultSort={{ field: 'forceRank', direction: 'asc' }}
        groupOptions={[
          { field: 'workDefinitionStatus', label: 'Decomposition' },
          { field: 'category', label: 'Category' },
        ]}
        facets={PRIORITY_FACETS}
        tableColumns={tableColumns}
        renderCard={(p, { selected }) => (
          <div className="p-4">
            <div className="flex items-start justify-between gap-2 min-w-0">
              <p className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{p.name}</p>
              <Link
                to={itemHref(p.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 text-[var(--text-ter)] hover:text-primary-600 dark:hover:text-primary-400"
                title="Open in Priorities"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </div>
            <p className="text-xs text-[var(--text-ter)] mt-1">
              Rank {p.forceRank ?? '—'} · ~{p.priorityLevelWuEstimate ?? '—'} CU
            </p>
            {selected && <p className="text-xs text-[var(--text-ter)] mt-2">Selected</p>}
          </div>
        )}
        renderDetail={(p) => (
          <PriorityDetailPanel
            priority={p}
            projectId={projectId}
            sourceItem={itemById.get(p.id)}
            summary={summaryByPriorityId.get(p.id)}
            inboundDeps={priorityDeps.filter((d) => d.toPriorityId === p.id)}
            outboundDeps={priorityDeps.filter((d) => d.fromPriorityId === p.id)}
            priorityById={priorityById}
            onUpdated={(saved, moveToRank) => {
              // Optimistically update this priority in the list so the table
              // reflects the new value immediately without waiting for a reload.
              setPriorities((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
              if (moveToRank != null) {
                void handleReorderByRank(saved, moveToRank).then(() => load());
              } else {
                // Background reload to sync any server-side changes.
                void load();
              }
            }}
            onDeleted={() => {
              setSelectedId(null);
              void load();
            }}
          />
        )}
        selectedId={selectedId}
        onSelectionChange={setSelectedId}
        urlParamName="priority"
        onRefresh={() => void load()}
        headerActions={
          <button
            type="button"
            onClick={() => setReorderOpen(true)}
            disabled={visiblePriorities.length < 2}
            title={visiblePriorities.length < 2 ? 'Need at least two priorities in scope to reorder' : undefined}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] border border-[var(--rule)] dark:border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ListOrdered className="h-4 w-4" />
            Reorder
          </button>
        }
        onImport={() => setImportOpen(true)}
        onExport={() =>
          exportCsv(
            `planning-priorities-${new Date().toISOString().slice(0, 10)}.csv`,
            ['id', 'name', 'force_rank', 'category', 'wu_estimate', 'work_definition_status'],
            priorities.map((p) => [
              p.id,
              p.name,
              String(p.forceRank ?? ''),
              p.category ?? '',
              String(p.priorityLevelWuEstimate ?? ''),
              p.workDefinitionStatus,
            ]),
          )
        }
        onCreate={() => setCreating(true)}
        createLabel="New priority"
        emptyTitle={
          scopedItemIds
            ? priorities.length > 0
              ? `Scope shows 0 of ${priorities.length} priorities`
              : `No items found for this ${engineInputKindLabel(engineInput.type)}`
            : 'No active items found'
        }
        emptyDescription={
          scopedItemIds
            ? priorities.length > 0
              ? `This scenario’s Input Scope (${engineInputKindLabel(engineInput.type)}) has no matching items. All Live Items is locked while the scenario is hard-scoped.`
              : 'Items in this scope may not exist yet or may have been removed. Switch scenario or change the bound pool — do not escape to All Live Items.'
            : 'Add items in Prioritization and they will appear here automatically.'
        }
        emptyAction={undefined}
        error={error}
        activeScenario={activeScenario}
        itemLabel="priority"
      />
      {creating && (
        <CreatePriorityModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); void load(); }}
        />
      )}

      {importOpen && (
        <ItemImportExportModal
          mode="import"
          items={storeItems}
          onClose={() => void handleImportClose()}
        />
      )}

      {reorderOpen && (
        <PriorityRankReorderModal
          priorities={visiblePriorities}
          onClose={() => setReorderOpen(false)}
          onReordered={(updated) => {
            const byId = new Map(updated.map((p) => [p.id, p]));
            setPriorities((prev) => prev.map((p) => byId.get(p.id) ?? p));
          }}
        />
      )}
    </>
  );
}
